import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";

import { ApprovalTimeline } from "../components/ApprovalTimeline";
import { FormFieldRenderer, evalCondition } from "../components/FormFieldRenderer";
import { Icon, type IconName } from "../components/Icon";
import { StatusPill } from "../components/StatusPill";
import { formatFieldValue, initialFormValues } from "../utils/formValues";
import { nextActionLine, requestTimeline } from "../utils/requestTimeline";
import {
  api,
  type AdminCompanyRow,
  type FormFieldSchema,
  type FormIconColor,
  type FormPublic,
  type FormSection,
  type FormSubmissionResult,
  type Me,
  type RequestDetail,
} from "../services/api";

type View = "form" | "review" | "confirm";

const ICON_COLORS: FormIconColor[] = ["violet", "green", "orange", "red", "blue"];

/** Mirror ServiceCatalog: a stable accent colour, schema-driven when set. */
function formIconColor(form: FormPublic): FormIconColor {
  const declared = form.form_schema.icon_color;
  if (declared && ICON_COLORS.includes(declared)) return declared;
  const seed = (form.category?.trim() || form.title || form.slug || "").trim();
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return ICON_COLORS[Math.abs(hash) % ICON_COLORS.length];
}

function formIcon(form: FormPublic): IconName {
  const hay = `${form.category ?? ""} ${form.title} ${form.slug}`.toLowerCase();
  if (/offboard|leaver|depart/.test(hay)) return "userMinus";
  if (/onboard|new starter|new-starter|joiner|new user/.test(hay)) return "userPlus";
  if (/access|permission|account|licen[cs]e/.test(hay)) return "key";
  if (/support|incident|help|issue|fault/.test(hay)) return "lifeBuoy";
  if (/hardware|device|laptop|equipment/.test(hay)) return "monitor";
  if (/security|mfa|phish/.test(hay)) return "shield";
  if (/mail|email|software/.test(hay)) return "mail";
  return "doc";
}

function isFilled(v: unknown): boolean {
  // A toggle (single_checkbox) left/turned OFF stores `false` — that is not an
  // affirmative answer, so it must not count toward section completion (else the
  // section badge goes green and stays green when the toggle is switched off).
  return !(
    v === undefined ||
    v === null ||
    v === false ||
    v === "" ||
    (Array.isArray(v) && v.length === 0)
  );
}

/**
 * Single-form submission flow (Brief v1.2 §7.2) in the refined "Online Forms"
 * look (design handoff §5.2): a two-column runner (numbered section cards + a
 * sticky progress / "what happens next" rail), a Review step with per-section
 * edit links and pill-value answers, and a Confirmation screen with the live
 * approval timeline. One route, three local states. Honours
 * show_when / required_when and field width; the server re-validates.
 */
export function FormSubmit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: form, error: loadErr } = useSWR<FormPublic>(
    id ? `/api/forms/${id}` : null,
    () => api.forms.get(id as string),
  );
  // Company-level attach gate: when the customer can't attach files, file_upload
  // fields are hidden across the runner (and so are dropped from validation +
  // the submitted payload). Treat undefined (older API) as enabled.
  const { data: me } = useSWR<Me>("/api/me", () => api.me());
  const canAttach = me?.can_attach !== false;

  // iQuest staff can file a form on behalf of a client, pushing a ticket
  // straight to that client's Autotask (bypassing approval). Clients never see
  // the picker and always file for their own company.
  const isStaff = me?.role === "iquest_admin" || me?.role === "service_desk";
  const { data: staffCompanies } = useSWR<AdminCompanyRow[]>(
    isStaff ? "/api/admin/forms/companies" : null,
    () => api.admin.forms.companies(),
  );

  const [values, setValues] = useState<Record<string, unknown>>({});
  // Seed choice-field defaults once the form arrives (issue #30). A ref guards
  // against re-seeding (and clobbering the user's edits) on later revalidations.
  const seededRef = useRef(false);
  useEffect(() => {
    if (form && !seededRef.current) {
      seededRef.current = true;
      setValues(initialFormValues(form.form_schema.fields));
    }
  }, [form]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [view, setView] = useState<View>("form");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<FormSubmissionResult | null>(null);
  const [targetCompanyId, setTargetCompanyId] = useState<string>("");

  const visibleFields = useMemo(() => {
    if (!form) return [];
    return form.form_schema.fields.filter(
      (f) =>
        (canAttach || f.type !== "file_upload") &&
        evalCondition(f.show_when ?? null, values),
    );
  }, [form, values, canAttach]);

  // Group visible fields by their declared section (presentational only).
  const sectionGroups = useMemo(() => {
    const sections: FormSection[] = form?.form_schema.sections ?? [];
    const known = new Set(sections.map((s) => s.id));
    const bySection = new Map<string, FormFieldSchema[]>();
    const ungrouped: FormFieldSchema[] = [];
    for (const field of visibleFields) {
      const sid = field.section_id;
      if (sid && known.has(sid)) {
        const bucket = bySection.get(sid) ?? [];
        bucket.push(field);
        bySection.set(sid, bucket);
      } else {
        ungrouped.push(field);
      }
    }
    const groups = sections
      .map((section) => ({ section, fields: bySection.get(section.id) ?? [] }))
      .filter((g) => g.fields.length > 0);
    return { hasSections: sections.length > 0, groups, ungrouped };
  }, [form, visibleFields]);

  // All section groups including any ungrouped tail (shared by runner + review).
  const allGroups = useMemo(() => {
    const tail = sectionGroups.ungrouped.length
      ? [{ section: { id: "__other", title: "Other" } as FormSection, fields: sectionGroups.ungrouped }]
      : [];
    return [...sectionGroups.groups, ...tail];
  }, [sectionGroups]);

  // Per-section completion (presentational — drives the section badge + rail).
  const groupStats = useMemo(
    () =>
      allGroups.map(({ section, fields }) => {
        const data = fields.filter((f) => f.type !== "section_header");
        const filled = data.filter((f) => isFilled(values[f.id])).length;
        return { section, total: data.length, filled, done: data.length > 0 && filled === data.length };
      }),
    [allGroups, values],
  );
  const currentGroupIdx = groupStats.findIndex((g) => !g.done);

  function validate(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const field of visibleFields) {
      if (field.type === "section_header") continue;
      const v = values[field.id];
      const isEmpty =
        v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
      const requiredNow =
        Boolean(field.required) || evalCondition(field.required_when ?? null, values);
      if (isEmpty && requiredNow) out[field.id] = `${field.label} is required.`;
    }
    return out;
  }

  function setValue(fieldId: string, v: unknown) {
    setValues((prev) => ({ ...prev, [fieldId]: v }));
    setErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }

  function goReview() {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setErrors({});
    setView("review");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function submit() {
    if (submitting || !form) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {};
      const visibleIds = new Set(visibleFields.map((f) => f.id));
      for (const [k, v] of Object.entries(values)) {
        if (visibleIds.has(k)) payload[k] = v;
      }
      const res = await api.forms.submit(
        form.id,
        payload,
        isStaff ? targetCompanyId || undefined : undefined,
      );
      setResult(res);
      setView("confirm");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (caught) {
      if (caught instanceof Error) {
        const parsed = tryParseErrorBody(caught.message);
        if (parsed) {
          setErrors(parsed);
          setView("form");
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          setSubmitError(caught.message);
        }
      } else {
        setSubmitError("Submission failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loadErr) {
    return (
      <div className="rf-wrap">
        <div className="page-header">
          <h1 className="page-title">Form not found</h1>
        </div>
        <div className="card form-error">{String(loadErr)}</div>
      </div>
    );
  }
  if (!form) {
    return (
      <div className="rf-wrap">
        <div className="page-header">
          <h1 className="page-title">Loading&hellip;</h1>
        </div>
        <div className="card">Loading form&hellip;</div>
      </div>
    );
  }

  const schema = form.form_schema;
  const hasApproval = Boolean(schema.approval_form_type_slug);
  const iconColor = formIconColor(form);
  const iconName = formIcon(form);

  if (view === "confirm" && result) {
    return (
      <Confirmation
        form={form}
        result={result}
        onTrack={() => navigate("/services/requests")}
        onNew={() => {
          setValues(initialFormValues(form.form_schema.fields));
          setErrors({});
          setResult(null);
          setView("form");
        }}
      />
    );
  }

  // ---- Review ----
  if (view === "review") {
    return (
      <div className="rf-wrap">
        <button type="button" className="rf-backlink" onClick={() => setView("form")}>
          <Icon name="arrowLeft" size={15} />
          Back to form
        </button>

        <div className="rf-runner">
          <div>
            <div className="rf-pagehead" style={{ marginBottom: 24 }}>
              <h1 className="rf-h1" style={{ fontSize: 26 }}>
                Review your request
              </h1>
              <p className="rf-sub">
                Check everything looks right before submitting. You can edit any section.
              </p>
            </div>

            {allGroups.map(({ section, fields }, i) => {
              const rows = fields
                .filter((f) => f.type !== "section_header")
                .map((f) => ({ f, val: formatFieldValue(f, values[f.id]) }))
                .filter((r) => r.val !== null);
              if (!rows.length) return null;
              return (
                <div className="rf-review-group" key={section.id}>
                  <div className="rf-review-head">
                    <span className="t">
                      <span className="num">{i + 1}</span>
                      {section.title}
                    </span>
                    <button type="button" className="rf-edit" onClick={() => setView("form")}>
                      Edit <Icon name="chevRight" size={13} />
                    </button>
                  </div>
                  <dl className="rf-review-rows">
                    {rows.map(({ f }) => (
                      <div className="rf-rev-row" key={f.id}>
                        <dt>{f.label}</dt>
                        <dd>{reviewValue(f, values[f.id])}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              );
            })}

            {isStaff && (
              <div className="rf-review-group">
                <div className="rf-review-head">
                  <span className="t">
                    <span className="num">
                      <Icon name="userPlus" size={13} />
                    </span>
                    File on behalf of a client
                  </span>
                </div>
                <div style={{ padding: "12px 16px" }}>
                  <select
                    className="rf-prop-select"
                    style={{ width: "100%" }}
                    value={targetCompanyId}
                    onChange={(e) => setTargetCompanyId(e.target.value)}
                  >
                    <option value="">Select a client…</option>
                    {(staffCompanies ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <p className="rf-prop-hint" style={{ marginBottom: 0 }}>
                    Submitting pushes a ticket straight into this client&apos;s
                    Autotask (no approval step). Requires Autotask writes to be
                    enabled for them.
                  </p>
                </div>
              </div>
            )}

            {submitError && <div className="form-error">{submitError}</div>}

            <div className="rf-actionbar">
              <span className="hint">
                {me?.impersonating
                  ? "Read-only preview — submitting is disabled while viewing as another user."
                  : isStaff
                    ? "Pick a client above — this creates an Autotask ticket directly, bypassing approval."
                    : hasApproval
                      ? "By submitting, this request is sent for approval before it's actioned."
                      : "This goes straight to the iQuest service desk."}
              </span>
              <div className="grp">
                <button type="button" className="btn btn-ghost" onClick={() => setView("form")}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={submit}
                  disabled={submitting || (isStaff && !targetCompanyId) || !!me?.impersonating}
                >
                  {submitting ? (
                    <>
                      <Icon name="clock" size={16} />
                      Submitting&hellip;
                    </>
                  ) : (
                    <>
                      <Icon name="send" size={16} />
                      Submit request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <aside className="rf-rail">
            <div className="rf-rail-card">
              <div className="rf-rail-title">Summary</div>
              <div className="rf-help-line">
                <Icon name={iconName} />
                <span>
                  <strong>{form.title}</strong>
                  {form.category ? ` · ${form.category}` : ""}
                </span>
              </div>
              {hasApproval && (
                <div className="rf-help-line">
                  <Icon name="shield" />
                  <span>Routed for approval before it&apos;s actioned.</span>
                </div>
              )}
              {schema.sla_target && (
                <div className="rf-help-line">
                  <Icon name="clock" />
                  <span>
                    Target turnaround <strong>{schema.sla_target}</strong>.
                  </span>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    );
  }

  // ---- Runner (form) ----
  const hasErrors = Object.keys(errors).length > 0;
  // user_picker fields list people from a company directory. For a client that's
  // always their own company; iQuest staff filing on behalf of a client scope it
  // to the chosen client so the picker shows that client's people, not iQuest's.
  const hasPeopleField = schema.fields.some((f) => f.type === "user_picker");
  const peopleCompanyId = isStaff ? targetCompanyId || null : null;
  const renderField = (field: FormFieldSchema) => (
    <FormFieldRenderer
      key={field.id}
      field={field}
      value={values[field.id]}
      onChange={(v) => setValue(field.id, v)}
      error={errors[field.id] ?? null}
      disabled={submitting}
      peopleCompanyId={peopleCompanyId}
    />
  );

  const railOutputs = schema.outputs ?? [];
  const showRail =
    sectionGroups.hasSections || hasApproval || Boolean(schema.sla_target) || railOutputs.length > 0;

  return (
    <div className="rf-wrap">
      <button type="button" className="rf-backlink" onClick={() => navigate("/services")}>
        <Icon name="arrowLeft" size={15} />
        All forms
      </button>

      <div className={showRail ? "rf-runner" : undefined}>
        <div>
          <div className="rf-formhead">
            <div className="rf-formhead-top">
              <span className={`rf-card-icon ${iconColor}`}>
                <Icon name={iconName} size={24} />
              </span>
              <div>
                {form.category && <div className="rf-card-cat">{form.category}</div>}
                <div className="rf-formhead-title">{form.title}</div>
                {form.description && <p className="rf-formhead-desc">{form.description}</p>}
              </div>
            </div>
            {(schema.est_time || hasApproval || schema.sla_target) && (
              <div className="rf-formhead-meta">
                {schema.est_time && (
                  <span className="rf-tag violet">
                    <Icon name="clock" size={12} />
                    {schema.est_time}
                  </span>
                )}
                {hasApproval && (
                  <span className="rf-tag approval">
                    <Icon name="shield" size={12} />
                    Needs approval
                  </span>
                )}
                {schema.sla_target && (
                  <span className="rf-meta">
                    <Icon name="clock" size={14} />
                    Target <strong>{schema.sla_target}</strong>
                  </span>
                )}
              </div>
            )}
          </div>

          {isStaff && hasPeopleField && (
            <div className="rf-section">
              <div className="rf-section-head">
                <span className="rf-section-num">
                  <Icon name="userPlus" size={15} />
                </span>
                <div className="rf-section-titles">
                  <div className="t">Filing on behalf of a client</div>
                  <div className="s">
                    Pick the client first so the people picker lists that
                    client&apos;s directory, not iQuest staff.
                  </div>
                </div>
              </div>
              <select
                className="rf-prop-select"
                style={{ width: "100%", maxWidth: 360 }}
                value={targetCompanyId}
                onChange={(e) => setTargetCompanyId(e.target.value)}
              >
                <option value="">Select a client…</option>
                {(staffCompanies ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {hasErrors && (
            <div className="iq-callout amber" style={{ marginBottom: 22 }}>
              <Icon name="alert" size={18} />
              <span>A few required fields need your attention &mdash; they&apos;re highlighted below.</span>
            </div>
          )}

          {sectionGroups.hasSections ? (
            allGroups.map(({ section, fields }, i) => {
              const stat = groupStats[i];
              const done = stat?.done;
              return (
                <div key={section.id} className="rf-section">
                  <div className="rf-section-head">
                    <span className={"rf-section-num" + (done ? " done" : "")}>
                      {done ? <Icon name="check" size={15} /> : i + 1}
                    </span>
                    <div className="rf-section-titles">
                      <div className="t">{section.title}</div>
                      {section.description && <div className="s">{section.description}</div>}
                    </div>
                  </div>
                  <div className="rf-fieldgrid">{fields.map(renderField)}</div>
                </div>
              );
            })
          ) : (
            <div className="rf-section">
              <div className="rf-fieldgrid">{visibleFields.map(renderField)}</div>
            </div>
          )}

          {submitError && <div className="form-error">{submitError}</div>}

          <div className="rf-actionbar">
            <span className="hint">Review your answers on the next step before submitting.</span>
            <div className="grp">
              <button type="button" className="btn btn-ghost" onClick={() => navigate("/services")}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={goReview}>
                Continue to review
                <Icon name="arrowRight" size={16} />
              </button>
            </div>
          </div>
        </div>

        {showRail && (
          <aside className="rf-rail">
            {sectionGroups.hasSections && (
              <div className="rf-rail-card">
                <div className="rf-rail-title">Your progress</div>
                <div className="rf-steps">
                  {groupStats.map((g, i) => {
                    const state = g.done
                      ? "done"
                      : i === currentGroupIdx
                        ? "current"
                        : "upcoming";
                    return (
                      <div className={`rf-step ${state}`} key={g.section.id}>
                        <div className="rf-step-rail">
                          <span className="rf-step-dot">
                            {g.done ? <Icon name="check" size={12} /> : i + 1}
                          </span>
                          <span className="rf-step-line" />
                        </div>
                        <div className="rf-step-body">
                          <div className="t">{g.section.title}</div>
                          <div className="s">
                            {g.total > 0 ? `${g.filled} of ${g.total} complete` : "Optional"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div
                    className={`rf-step ${currentGroupIdx === -1 ? "current" : "upcoming"}`}
                  >
                    <div className="rf-step-rail">
                      <span className="rf-step-dot">
                        <Icon name="check" size={12} />
                      </span>
                    </div>
                    <div className="rf-step-body">
                      <div className="t">Review &amp; submit</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="rf-rail-card">
              <div className="rf-rail-title">What happens next</div>
              {hasApproval && (
                <div className="rf-help-line">
                  <Icon name="shield" />
                  <span>Your request is routed for approval before it&apos;s actioned.</span>
                </div>
              )}
              <div className="rf-help-line">
                <Icon name="refresh" />
                <span>The iQuest service desk picks it up and provisions what you asked for.</span>
              </div>
              {schema.sla_target && (
                <div className="rf-help-line">
                  <Icon name="clock" />
                  <span>
                    Typical turnaround is <strong>{schema.sla_target}</strong>.
                  </span>
                </div>
              )}
              {railOutputs.map((o) => (
                <div className="rf-help-line" key={o}>
                  <Icon name="check" />
                  <span>{o}</span>
                </div>
              ))}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

/** Review value: multi-value answers render as `.pillval` chips, else text. */
function reviewValue(field: FormFieldSchema, value: unknown) {
  if (
    (field.type === "checkbox_group" || field.type === "multi_select") &&
    Array.isArray(value) &&
    value.length > 0
  ) {
    return (value as unknown[]).map((v) => {
      const label = field.options?.find((o) => o.value === v)?.label ?? String(v);
      return (
        <span className="pillval" key={String(v)}>
          {label}
        </span>
      );
    });
  }
  return formatFieldValue(field, value);
}

/** Confirmation screen — fetches the created request for the live timeline. */
function Confirmation({
  form,
  result,
  onTrack,
  onNew,
}: {
  form: FormPublic;
  result: FormSubmissionResult;
  onTrack: () => void;
  onNew: () => void;
}) {
  const navigate = useNavigate();
  const { data: detail } = useSWR<RequestDetail>(
    result.approval_request_id ? `/api/requests/${result.approval_request_id}` : null,
    () => api.requests.get(result.approval_request_id as string),
  );

  const timeline = detail ? requestTimeline(detail) : [];
  const pushedTicket = result.autotask_ticket_number;
  const note = pushedTicket
    ? "Pushed straight to Autotask. The ticket is now in the client's service desk queue."
    : detail
      ? nextActionLine(detail)
      : "Received by the iQuest service desk. Track its progress under My Requests.";
  const ref = pushedTicket ?? result.approval_request_id ?? result.id;

  return (
    <div className="rf-wrap">
      <div className="rf-confirm">
        <div className="rf-confirm-badge">
          <Icon name="check" size={38} />
        </div>
        <h1>Request submitted</h1>
        <p className="lede">
          Your <strong>{form.title}</strong> has been received. {note}
        </p>
        <div className="rf-ref">
          <span className="k">{pushedTicket ? "AUTOTASK TICKET" : "REQUEST"}</span>
          <span className="v">{ref}</span>
        </div>

        {detail && (
          <div className="rf-confirm-card">
            <div className="rf-rail-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              Approval &amp; provisioning
              <StatusPill state={detail.state} />
            </div>
            {timeline.length > 0 ? (
              <ApprovalTimeline path={timeline} />
            ) : (
              <p style={{ fontSize: 13, color: "var(--iq-text-muted)", margin: 0 }}>
                We&apos;ll keep you posted at every step under My Requests.
              </p>
            )}
          </div>
        )}

        <div className="rf-confirm-actions">
          <button type="button" className="btn btn-ghost" onClick={onNew}>
            <Icon name="plus" size={16} />
            Submit another
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={pushedTicket ? () => navigate(`/tickets/${pushedTicket}`) : onTrack}
          >
            {pushedTicket ? "View ticket" : "View request"}
            <Icon name="arrowRight" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function tryParseErrorBody(raw: string): Record<string, string> | null {
  const idx = raw.indexOf(": ");
  const body = idx >= 0 ? raw.slice(idx + 2) : raw;
  try {
    const parsed = JSON.parse(body);
    if (
      parsed &&
      typeof parsed === "object" &&
      parsed.detail &&
      typeof parsed.detail === "object" &&
      parsed.detail.errors &&
      typeof parsed.detail.errors === "object"
    ) {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed.detail.errors)) {
        out[k] = String(v);
      }
      return out;
    }
  } catch {
    return null;
  }
  return null;
}
